import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Row, Col, InputGroup, Form, PageHeader } from '@/components/ui';
import { Task, TaskType, TaskStage } from '../../../types/Task';
import { RoutineTask } from '../../../types/RoutineTask';
import { Effort } from '../../../types/Effort';
import { Project } from '../../../types/Project';
import { ForemanArea } from '../../../types/ForemanArea';
import { allTasksService, AllTasksFilters, AllTasksFilterOptions } from '../../../services/allTasksService';
import { areasService } from '../../../services/areasService';
import { routineTaskService } from '../../../services/routineTaskService';
import { taskService } from '../../../services/taskService';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import ServerPagination from '../../../components/ServerPagination';
import AllTasksFiltersPanel from '../components/AllTasksFilters';
import ProjectWorksTable from '../../projects/components/ProjectWorksTable';
import RoutineTaskModal from '../../../components/RoutineTaskModal';
import CustomTaskModal from '../../../components/CustomTaskModal';
import TaskModal from '../../../components/QPTaskModal';
import QuantifiedTaskModal from '../../quantified-tracking/components/taskModals/TaskModal';
import CloseRoutineTaskModal from '../../projects/components/CloseRoutineTaskModal';
import CancelTaskConfirmationModal from '../../../components/CancelTaskConfirmationModal';
import ConvertTaskTypeModal, { ConvertDirection } from '../../projects/components/ConvertTaskTypeModal';
import TaskHistoryModal from '../../../components/TaskHistoryModal';
import CloneTaskConfirmModal from '../../../components/CloneTaskConfirmModal';
import PrintTaskPDFModal from '../../quantified-tracking/components/PrintTaskPDFModal';
import CompletionModal from '../../quantified-tracking/components/taskModals/CompletionModal';
import CloseTaskModal from '../../quantified-tracking/components/taskModals/CloseTaskModal';
import ReopenTaskModal from '../../quantified-tracking/components/taskModals/ReopenTaskModal';
import SetInternalReviewModal from '../../quantified-tracking/components/taskModals/SetInternalReviewModal';
import { ContractArea } from '../../../types/ContractArea';

const parseArrayParam = (param: string | null): string[] => {
  if (!param) return [];
  return param.split(',').filter((v) => v);
};

const AllTasksPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();

  const canEditTask =
    hasPermission('quantified:task_edit') ||
    hasPermission('project:task_edit') ||
    hasPermission('routine:specific_create');
  const canCreateTask =
    hasPermission('project:task_create') ||
    hasPermission('quantified:task_create') ||
    hasPermission('routine:specific_create');
  const canCloneTask = canCreateTask || canEditTask;
  const canCloseTask = hasPermission('project:task_close');
  const canReopenTask = hasPermission('project:task_close');
  const canCancelTask = hasPermission('project:task_cancel') || hasPermission('project:task_edit');
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);

  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  /**
   * Sort lives here rather than inside the table because the list is server-paginated: the
   * table only ever holds one page, so ordering has to happen in the query. `null` means the
   * server's default order.
   */
  const [sort, setSort] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' } | null>(null);


  // Every list filter is multiselect, so the canonical in-memory shape is an array.
  const [filters, setFilters] = useState<AllTasksFilters>({
    serviceArea: [],
    projectId: [],
    taskType: [],
    status: [],
    foremanArea: [],
    contractYear: [],
    activityId: [],
    search: ''
  });
  const [pendingFilters, setPendingFilters] = useState<AllTasksFilters>({ ...filters });

  const [filterOptions, setFilterOptions] = useState<AllTasksFilterOptions>({
    statuses: [],
    taskTypes: [],
    serviceAreas: [],
    contractYears: [],
    projects: [],
    foremanAreas: [],
    activities: []
  });

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedRoutineTask, setSelectedRoutineTask] = useState<RoutineTask | null>(null);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showRoutineTaskModal, setShowRoutineTaskModal] = useState(false);
  const [showCustomTaskModal, setShowCustomTaskModal] = useState(false);
  const [showQuantifiedTaskModal, setShowQuantifiedTaskModal] = useState(false);
  const [isCloseTaskModalVisible, setIsCloseTaskModalVisible] = useState(false);
  const [selectedTaskForClose, setSelectedTaskForClose] = useState<Task | null>(null);
  const [isQuantifiedCloseModalVisible, setIsQuantifiedCloseModalVisible] = useState(false);
  const [selectedTaskForQuantifiedClose, setSelectedTaskForQuantifiedClose] = useState<Task | null>(null);
  const [isCompletionModalVisible, setIsCompletionModalVisible] = useState(false);
  const [selectedTaskForCompletion, setSelectedTaskForCompletion] = useState<Task | null>(null);
  const [isReopenTaskModalVisible, setIsReopenTaskModalVisible] = useState(false);
  const [selectedTaskForReopen, setSelectedTaskForReopen] = useState<Task | null>(null);
  const [isSetInternalReviewModalVisible, setIsSetInternalReviewModalVisible] = useState(false);
  const [selectedTaskForInternalReview, setSelectedTaskForInternalReview] = useState<Task | null>(null);
  const [isCancelTaskModalVisible, setIsCancelTaskModalVisible] = useState(false);
  const [selectedTaskForCancel, setSelectedTaskForCancel] = useState<Task | null>(null);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<Task | null>(null);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [selectedTaskForConvert, setSelectedTaskForConvert] = useState<Task | null>(null);
  const [convertDirection, setConvertDirection] = useState<ConvertDirection | null>(null);
  const [cloneConfirmTask, setCloneConfirmTask] = useState<Task | null>(null);
  const [printPdfTask, setPrintPdfTask] = useState<Task | null>(null);

  const taskIdFromUrl = searchParams.get('taskId');
  const fetchGenerationRef = useRef(0);

  const mergeVehicleFuelEffort = useCallback((effort: Effort) => {
    if (effort.taskId == null) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id !== effort.taskId
          ? t
          : {
              ...t,
              efforts: (t.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
            }
      )
    );
  }, []);

  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize') || '20', 10);
    // parseArrayParam accepts a bare single value too, so pre-existing deep links keep working.
    const filtersFromUrl: AllTasksFilters = {
      serviceArea: parseArrayParam(searchParams.get('serviceArea')),
      projectId: parseArrayParam(searchParams.get('projectId')),
      taskType: parseArrayParam(searchParams.get('taskType')),
      status: parseArrayParam(searchParams.get('status')),
      foremanArea: parseArrayParam(searchParams.get('foremanArea')),
      contractYear: parseArrayParam(searchParams.get('contractYear')),
      activityId: parseArrayParam(searchParams.get('activityId')),
      search: searchParams.get('search') || ''
    };
    setPagination((prev) => ({ ...prev, page: pageFromUrl, pageSize: pageSizeFromUrl }));
    setFilters(filtersFromUrl);
    setPendingFilters(filtersFromUrl);
    setSearchTerm(filtersFromUrl.search || '');
    setSearchInput(filtersFromUrl.search || '');
  }, [searchParams]);

  const fetchTasks = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    try {
      setLoading(true);
      const filterByTaskId = taskIdFromUrl && /^\d+$/.test(taskIdFromUrl);
      const combinedFilters: AllTasksFilters = {
        ...filters,
        search: filterByTaskId ? searchTerm || taskIdFromUrl : searchTerm,
        ...(filterByTaskId && taskIdFromUrl ? { taskId: taskIdFromUrl } : {})
      };
      const pageToUse = filterByTaskId ? 1 : pagination.page;
      const response = await allTasksService.getAllTasksPaginated(
        pageToUse,
        pagination.pageSize,
        combinedFilters,
        sort ?? undefined
      );
      if (generation !== fetchGenerationRef.current) return;
      setTasks(response.data);
      setPagination((prev) => ({ ...prev, ...response.pagination }));
    } catch (error) {
      if (generation === fetchGenerationRef.current) {
        console.error('Error fetching all tasks:', error);
      }
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [filters, searchTerm, taskIdFromUrl, pagination.page, pagination.pageSize, sort]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    fetchFilterOptions();
    fetchForemanAreas();
  }, []);

  const fetchForemanAreas = async () => {
    try {
      const areas = await areasService.getForemanAreas();
      const converted = areas.map((area) => ({
        id: area.id,
        name: area.name,
        serviceAreaId: area.serviceArea?.id,
        serviceArea: area.serviceArea
          ? { ...area.serviceArea, foremanAreas: [], activities: [] }
          : undefined,
        contractAreas: area.contractAreas || []
      }));
      setForemanAreas(converted as unknown as ForemanArea[]);
    } catch (error) {
      console.error('Error fetching foreman areas:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await allTasksService.getAllTasksFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    setSort({ sortBy: field, sortOrder: direction });
    // A new ordering invalidates the current offset — go back to page 1 rather than showing
    // page 7 of a different sequence.
    updateUrlParams({ page: 1 });
  };

  const updateUrlParams = (
    newPagination?: Partial<typeof pagination>,
    newFilters?: AllTasksFilters
  ) => {
    const params = new URLSearchParams(searchParams);
    const preserveTaskId = searchParams.get('taskId');

    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }

    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) params.set(key, value.join(','));
          else params.delete(key);
        } else if (value) {
          params.set(key, value as string);
        } else {
          params.delete(key);
        }
      });
    }

    if (preserveTaskId) params.set('taskId', preserveTaskId);
    setSearchParams(params);
  };

  const handleFilterChange = (name: string, value: string | string[] | boolean) => {
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    updateUrlParams({ page: 1 }, pendingFilters);
  };

  const handleClearFilters = () => {
    const cleared: AllTasksFilters = {
      serviceArea: [],
      projectId: [],
      taskType: [],
      status: [],
      foremanArea: [],
      contractYear: [],
      activityId: [],
      search: ''
    };
    setPendingFilters(cleared);
    setFilters(cleared);
    setSearchTerm('');
    setSearchInput('');
    updateUrlParams({ page: 1 }, cleared);
  };

  const hasActiveFilters = () =>
    Object.entries(filters).some(([key, value]) => {
      if (key === 'search') return false;
      if (Array.isArray(value)) return value.length > 0;
      return value !== '';
    });

  const hasUnappliedChanges = () => {
    const compareArrays = (a: string | string[] | undefined, b: string | string[] | undefined) => {
      const arrA = Array.isArray(a) ? a : a ? [a] : [];
      const arrB = Array.isArray(b) ? b : b ? [b] : [];
      return arrA.length !== arrB.length || !arrA.every((v, i) => v === arrB[i]);
    };
    return (
      compareArrays(pendingFilters.serviceArea, filters.serviceArea) ||
      compareArrays(pendingFilters.projectId, filters.projectId) ||
      compareArrays(pendingFilters.taskType, filters.taskType) ||
      compareArrays(pendingFilters.contractYear, filters.contractYear) ||
      compareArrays(pendingFilters.activityId, filters.activityId) ||
      compareArrays(pendingFilters.status, filters.status) ||
      compareArrays(pendingFilters.foremanArea, filters.foremanArea)
    );
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateUrlParams({ page: 1, pageSize });
  };

  const toggleSearch = () => setIsSearchVisible((prev) => !prev);

  const handleSearchInputChange = (value: string) => setSearchInput(value);

  const handleSearch = () => {
    const next = { ...filters, search: searchInput };
    setSearchTerm(searchInput);
    setFilters(next);
    updateUrlParams({ page: 1 }, next);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    const next = { ...filters, search: '' };
    setFilters(next);
    updateUrlParams({ page: 1 }, next);
  };

  const resolveProjectForTask = (task: Task): Project | null => {
    if (task.project) return task.project as Project;
    if (task.projectId) {
      return { id: task.projectId, name: '', type: 'Routine' } as Project;
    }
    return null;
  };

  const loadContractAreasForProject = async (project: Project) => {
    const fa = foremanAreas.find((a) => a.serviceAreaId === project.serviceAreaId);
    setContractAreas((fa?.contractAreas as ContractArea[]) || []);
  };

  const closeAllModals = () => {
    setShowTaskModal(false);
    setShowRoutineTaskModal(false);
    setShowCustomTaskModal(false);
    setShowQuantifiedTaskModal(false);
    setSelectedTask(null);
    setSelectedRoutineTask(null);
  };

  const handleEditTask = async (task: Task) => {
    closeAllModals();
    const project = resolveProjectForTask(task);
    setActiveProject(project);
    if (project) {
      await loadContractAreasForProject(project);
    } else {
      setContractAreas([]);
    }

    if (task.type === 'Routine') {
      const details = await routineTaskService.getTaskDetails(task.id);
      setSelectedRoutineTask(details);
      setShowRoutineTaskModal(true);
    } else if (task.type === 'Custom') {
      setSelectedTask(task);
      setShowCustomTaskModal(true);
    } else if (task.type === 'Quantified') {
      try {
        const fullTask = await quantifiedTaskService.getTask(task.id);
        setSelectedTask(fullTask);
      } catch {
        setSelectedTask(task);
      }
      setShowQuantifiedTaskModal(true);
    } else {
      setSelectedTask(task);
      setShowTaskModal(true);
    }
  };

  useEffect(() => {
    if (!taskIdFromUrl) return;
    const taskId = parseInt(taskIdFromUrl, 10);
    if (isNaN(taskId)) return;
    let cancelled = false;
    taskService
      .getTask(taskId)
      .then((task) => {
        if (!cancelled) {
          void handleEditTask(task as Task);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load task for taskId:', taskId, err);
      });
    return () => {
      cancelled = true;
    };
  }, [taskIdFromUrl]);

  const handleTaskSaved = async () => {
    closeAllModals();
    await fetchTasks();
  };

  const handleViewHistory = async (task: Task) => {
    try {
      let refreshedTask: Task;
      if (task.type === 'Routine') {
        const routineTask = await routineTaskService.getTaskDetails(task.id);
        refreshedTask = { ...task, taskHistory: routineTask.taskHistory || [] };
      } else {
        try {
          refreshedTask = await quantifiedTaskService.getTask(task.id);
        } catch {
          const regularTask = await taskService.getTask(task.id);
          refreshedTask = { ...task, taskHistory: regularTask.taskHistory || task.taskHistory || [] };
        }
      }
      setSelectedTaskForHistory(refreshedTask);
      setIsHistoryModalVisible(true);
    } catch {
      setSelectedTaskForHistory(task);
      setIsHistoryModalVisible(true);
    }
  };

  const handleCloseTask = (task: Task) => {
    if (task.type === 'Quantified') {
      setSelectedTaskForQuantifiedClose(task);
      setIsQuantifiedCloseModalVisible(true);
      return;
    }
    setSelectedTaskForClose(task);
    setIsCloseTaskModalVisible(true);
  };

  const handleConfirmClose = async (comments: string) => {
    try {
      if (selectedTaskForClose?.type === 'Routine') {
        await routineTaskService.updateTaskStatus(
          selectedTaskForClose.id,
          'CLOSED',
          comments || 'Closed from All Tasks'
        );
        await fetchTasks();
      }
    } catch (error) {
      console.error('Error closing task:', error);
      alert('Failed to close task');
    } finally {
      setIsCloseTaskModalVisible(false);
      setSelectedTaskForClose(null);
    }
  };

  const handleConfirmQuantifiedClose = async (comments: string) => {
    if (!selectedTaskForQuantifiedClose) return;
    await quantifiedTaskService.closeTask(selectedTaskForQuantifiedClose.id, {
      comments: comments || 'Task closed',
      userId: currentUser?.id,
      taskId: selectedTaskForQuantifiedClose.id
    });
    await fetchTasks();
    setIsQuantifiedCloseModalVisible(false);
    setSelectedTaskForQuantifiedClose(null);
  };

  const handleReportAccomplishment = (task: Task) => {
    setSelectedTaskForCompletion(task);
    setIsCompletionModalVisible(true);
  };

  const handleSetInternalReview = (task: Task) => {
    setSelectedTaskForInternalReview(task);
    setIsSetInternalReviewModalVisible(true);
  };

  const handleSetActive = async (task: Task) => {
    try {
      if (!currentUser?.id) throw new Error('User not authenticated');
      await quantifiedTaskService.updateTaskStatus(task.id, {
        status: TaskStage.ACTIVE,
        comments: 'Task set to active from All Tasks',
        userId: currentUser.id,
        taskId: task.id
      });
      await fetchTasks();
    } catch (error) {
      console.error('Error setting task to active:', error);
      alert(error instanceof Error ? error.message : 'Failed to set task to active');
    }
  };

  const handleReopenTask = async (task: Task) => {
    if (task.type === 'Quantified') {
      setSelectedTaskForReopen(task);
      setIsReopenTaskModalVisible(true);
      return;
    }
    try {
      if (task.type === 'Routine') {
        await routineTaskService.updateTaskStatus(task.id, 'ACTIVE', 'Reopened from All Tasks');
      } else if (task.type === 'Custom' || task.type === 'Overhead') {
        await taskService.updateTask(task.id.toString(), {
          description: task.description || '',
          type: task.type,
          status: 'ACTIVE',
          projectId: task.projectId ?? task.project?.id ?? 0,
          name: task.name,
          foremanAreaId: task.foremanAreaId ?? undefined
        });
      } else {
        return;
      }
      await fetchTasks();
    } catch (error) {
      console.error('Error reopening task:', error);
      alert('Failed to reopen task');
    }
  };

  const handleConfirmQuantifiedReopen = async (comments: string) => {
    if (!selectedTaskForReopen) return;
    await quantifiedTaskService.reopenTask(selectedTaskForReopen.id, {
      comments,
      userId: currentUser?.id,
      taskId: selectedTaskForReopen.id
    });
    await fetchTasks();
    setIsReopenTaskModalVisible(false);
    setSelectedTaskForReopen(null);
  };

  const handleCancelTask = (task: Task) => {
    setSelectedTaskForCancel(task);
    setIsCancelTaskModalVisible(true);
  };

  const handleConfirmCancel = async () => {
    try {
      if (selectedTaskForCancel && currentUser?.id) {
        await taskService.cancelTask(
          selectedTaskForCancel.id.toString(),
          'Task cancelled from All Tasks',
          currentUser.id
        );
        await fetchTasks();
      }
    } catch (error) {
      console.error('Error cancelling task:', error);
      alert('Failed to cancel task');
    } finally {
      setIsCancelTaskModalVisible(false);
      setSelectedTaskForCancel(null);
    }
  };

  const handleCloneTask = async (task: Task, nameOverride?: string) => {
    try {
      setLoading(true);
      await taskService.cloneTask(
        task.id,
        nameOverride != null && nameOverride.trim() !== '' ? { name: nameOverride.trim() } : undefined
      );
      await fetchTasks();
    } catch (error) {
      console.error('Error cloning task:', error);
      alert(error instanceof Error ? error.message : 'Failed to clone task');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToQuantified = (task: Task) => {
    setSelectedTaskForConvert(task);
    setConvertDirection('toQuantified');
    setIsConvertModalVisible(true);
  };

  const handleConvertToCustom = (task: Task) => {
    setSelectedTaskForConvert(task);
    setConvertDirection('toCustom');
    setIsConvertModalVisible(true);
  };

  const handleConfirmConvert = async (payload: {
    targetType: 'Quantified' | 'Custom';
    unitOfMeasure?: string;
    identifiedQuantity?: number;
  }) => {
    if (!selectedTaskForConvert) return;
    await taskService.convertTaskType(selectedTaskForConvert.id, payload);
    await fetchTasks();
    setIsConvertModalVisible(false);
    setSelectedTaskForConvert(null);
    setConvertDirection(null);
  };

  const handlePrintTaskPDF = (task: Task) => {
    setPrintPdfTask(task);
  };

  return (
    <div className="all-tasks-page tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      <PageHeader
        title="All Tasks"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => {
              setLoading(true);
              Promise.all([fetchTasks(), fetchFilterOptions()]).finally(() => setLoading(false));
            },
          },
          {
            key: 'search',
            label: `Search${searchTerm ? ` (${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '…' : searchTerm})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!searchTerm,
            title: isSearchVisible ? 'Close search' : 'Search tasks',
            onClick: toggleSearch,
          },
        ]}
      />

      <div className="tw-shrink-0">
        <AllTasksFiltersPanel
          filterOptions={filterOptions}
          filters={pendingFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          showApplyButton={true}
          onApply={handleApplyFilters}
          applyDisabled={!hasUnappliedChanges()}
          hasActiveFilters={hasActiveFilters()}
        />
      </div>

      {isSearchVisible && (
        <Row className="tw-mb-3 tw-shrink-0">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="tw-border-primary/40"
                placeholder="Search by task number, name, description, or project..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={handleSearchKeyPress}
                autoFocus
              />
              <Button variant="primary" type="button" onClick={handleSearch} title="Search">
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || searchTerm) && (
                <Button variant="outline-primary" type="button" onClick={clearSearch} title="Clear search">
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">Searching for: &quot;{searchTerm}&quot;</div>
            )}
          </Col>
        </Row>
      )}

      <div className="tw-grow table-scroll-wrapper" style={{ minWidth: 0, minHeight: 0 }}>
        <ProjectWorksTable
          tasks={tasks}
          loading={loading}
          sortField={sort?.sortBy ?? null}
          sortDirection={sort?.sortOrder ?? 'desc'}
          onSort={handleSortChange}
          canEditTask={canEditTask}
          canCloneTask={canCloneTask}
          canCloseTask={canCloseTask}
          canReopenTask={canReopenTask}
          canCancelTask={canCancelTask}
          canToggleVehicleFuel={canToggleVehicleFuel}
          onViewHistory={handleViewHistory}
          onEditTask={handleEditTask}
          onTaskClick={handleEditTask}
          onCloneTask={(t) => setCloneConfirmTask(t)}
          onCloseTask={handleCloseTask}
          onReopenTask={handleReopenTask}
          onCancelTask={handleCancelTask}
          onPrintPDF={handlePrintTaskPDF}
          onConvertToQuantified={canEditTask ? handleConvertToQuantified : undefined}
          onConvertToCustom={canEditTask ? handleConvertToCustom : undefined}
          onReportAccomplishment={handleReportAccomplishment}
          onSetInternalReview={handleSetInternalReview}
          onSetActive={handleSetActive}
          onVehicleFuelEffortPatched={mergeVehicleFuelEffort}
        />
      </div>

      <div className="tw-shrink-0">
        <ServerPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          className="tw-mt-3 tw-mb-4"
        />
        <div style={{ height: '20px' }} />
      </div>

      {showRoutineTaskModal && (
        <RoutineTaskModal
          visible={showRoutineTaskModal}
          onClose={() => {
            setShowRoutineTaskModal(false);
            setSelectedRoutineTask(null);
          }}
          task={selectedRoutineTask || undefined}
          onTaskSaved={handleTaskSaved}
          project={activeProject ?? undefined}
        />
      )}

      {showQuantifiedTaskModal && (
        <QuantifiedTaskModal
          taskType="Quantified"
          visible={showQuantifiedTaskModal}
          onClose={() => {
            setShowQuantifiedTaskModal(false);
            setSelectedTask(null);
          }}
          task={selectedTask || undefined}
          onTaskSaved={handleTaskSaved}
          foremanAreas={foremanAreas}
          project={activeProject ?? undefined}
          initialContractAreas={contractAreas}
        />
      )}

      {showTaskModal && (
        <TaskModal
          visible={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTask(null);
          }}
          task={selectedTask || undefined}
          onTaskSaved={handleTaskSaved}
          foremanAreas={foremanAreas}
          taskType={(selectedTask?.type as TaskType) || 'Overhead'}
          project={activeProject ?? undefined}
          initialContractAreas={contractAreas}
        />
      )}

      {showCustomTaskModal && (
        <CustomTaskModal
          visible={showCustomTaskModal}
          onClose={() => {
            setShowCustomTaskModal(false);
            setSelectedTask(null);
          }}
          onSave={handleTaskSaved}
          task={selectedTask}
          project={activeProject ?? undefined}
        />
      )}

      {selectedTaskForClose && (
        <CloseRoutineTaskModal
          visible={isCloseTaskModalVisible}
          onClose={() => {
            setIsCloseTaskModalVisible(false);
            setSelectedTaskForClose(null);
          }}
          onSubmit={handleConfirmClose}
          task={selectedTaskForClose}
        />
      )}

      {selectedTaskForQuantifiedClose && (
        <CloseTaskModal
          visible={isQuantifiedCloseModalVisible}
          onClose={() => {
            setIsQuantifiedCloseModalVisible(false);
            setSelectedTaskForQuantifiedClose(null);
          }}
          task={selectedTaskForQuantifiedClose}
          onSubmit={handleConfirmQuantifiedClose}
        />
      )}

      {selectedTaskForCompletion && (
        <CompletionModal
          visible={isCompletionModalVisible}
          onClose={() => {
            setIsCompletionModalVisible(false);
            setSelectedTaskForCompletion(null);
          }}
          task={selectedTaskForCompletion}
          onComplete={async () => {
            await fetchTasks();
            setIsCompletionModalVisible(false);
            setSelectedTaskForCompletion(null);
          }}
        />
      )}

      {selectedTaskForReopen && (
        <ReopenTaskModal
          visible={isReopenTaskModalVisible}
          onClose={() => {
            setIsReopenTaskModalVisible(false);
            setSelectedTaskForReopen(null);
          }}
          task={selectedTaskForReopen}
          onSubmit={handleConfirmQuantifiedReopen}
        />
      )}

      {selectedTaskForInternalReview && (
        <SetInternalReviewModal
          visible={isSetInternalReviewModalVisible}
          onClose={() => {
            setIsSetInternalReviewModalVisible(false);
            setSelectedTaskForInternalReview(null);
          }}
          task={selectedTaskForInternalReview}
          onSubmit={async () => {
            await fetchTasks();
            setIsSetInternalReviewModalVisible(false);
            setSelectedTaskForInternalReview(null);
          }}
        />
      )}

      <CloneTaskConfirmModal
        show={cloneConfirmTask !== null}
        onHide={() => setCloneConfirmTask(null)}
        onConfirm={(names) => {
          const t = cloneConfirmTask;
          setCloneConfirmTask(null);
          if (t) void handleCloneTask(t, names[0]);
        }}
        items={
          cloneConfirmTask
            ? [
                {
                  taskId: cloneConfirmTask.id,
                  sourceTaskLabel: cloneConfirmTask.taskNumber || `#${cloneConfirmTask.id}`,
                  sourceTaskName: cloneConfirmTask.name
                }
              ]
            : []
        }
      />

      <CancelTaskConfirmationModal
        visible={isCancelTaskModalVisible}
        onClose={() => {
          setIsCancelTaskModalVisible(false);
          setSelectedTaskForCancel(null);
        }}
        onConfirm={handleConfirmCancel}
        taskName={selectedTaskForCancel?.name || selectedTaskForCancel?.taskNumber}
      />

      <ConvertTaskTypeModal
        visible={isConvertModalVisible}
        task={selectedTaskForConvert}
        direction={convertDirection}
        onClose={() => {
          setIsConvertModalVisible(false);
          setSelectedTaskForConvert(null);
          setConvertDirection(null);
        }}
        onConfirm={handleConfirmConvert}
      />

      {selectedTaskForHistory && (
        <TaskHistoryModal
          visible={isHistoryModalVisible}
          onClose={() => {
            setIsHistoryModalVisible(false);
            setSelectedTaskForHistory(null);
          }}
          task={selectedTaskForHistory}
          onAddComment={() => fetchTasks()}
        />
      )}

      <PrintTaskPDFModal
        task={printPdfTask}
        onClose={() => setPrintPdfTask(null)}
      />
    </div>
  );
};

export default AllTasksPage;
